import { hmac } from "@noble/hashes/hmac"
import { sha256 } from "@noble/hashes/sha2"

// Synchronous replacement for the subset of `jsonwebtoken` this app uses; avoids shipping
// the ~500 KB crypto-browserify polyfill to every page.

const encoder = new TextEncoder()
const decoder = new TextDecoder()

function base64UrlToBytes(input: string): Uint8Array {
  const base64 = input.replace(/-/g, "+").replace(/_/g, "/")
  const binary = atob(base64 + "=".repeat((4 - (base64.length % 4)) % 4))
  return Uint8Array.from(binary, (char) => char.charCodeAt(0))
}

function parseSegment<T>(segment: string): T {
  return JSON.parse(decoder.decode(base64UrlToBytes(segment)))
}

/** Payload without signature check, like `jwt.decode`. */
export function decodeJwt<T>(token: string): T | null {
  try {
    return parseSegment<T>(token.split(".")[1])
  } catch {
    return null
  }
}

/** Payload if `jwt.verify(token, secret, { algorithms: ["HS256"] })` would accept it, else null. */
export function verifyJwt<T>(token: string, secret: string): T | null {
  const parts = token.split(".")
  if (parts.length !== 3) return null

  try {
    if (parseSegment<{ alg?: string }>(parts[0]).alg !== "HS256") return null

    const expected = hmac(sha256, encoder.encode(secret), encoder.encode(`${parts[0]}.${parts[1]}`))
    const actual = base64UrlToBytes(parts[2])
    if (actual.length !== expected.length) return null
    let diff = 0
    for (let i = 0; i < actual.length; i++) diff |= actual[i] ^ expected[i]
    if (diff !== 0) return null

    const payload = parseSegment<{ exp?: unknown; nbf?: unknown }>(parts[1])
    if (typeof payload !== "object" || payload === null) return null

    const now = Math.floor(Date.now() / 1000)
    if (payload.exp !== undefined && (typeof payload.exp !== "number" || now >= payload.exp)) return null
    if (payload.nbf !== undefined && (typeof payload.nbf !== "number" || payload.nbf > now)) return null

    return payload as T
  } catch {
    return null
  }
}
